// =============================================================================
// NAVBAR — Main Navigation Component
// =============================================================================
// Top navigation bar engineered for clear differentiation across ALL screen sizes:
//
// 1. CLEAR VISUAL DIFFERENTIATION:
//    Always renders with a distinct background (sleek translucent glassmorphism
//    over hero, solid themed surface when scrolled) with crisp border and contrast.
//
// 2. ZERO TEXT WRAPPING:
//    Brand name and navigation items are locked to a single line (`white-space: nowrap`)
//    with overflow protection.
//
// 3. RESPONSIVE HAMBURGER (< 1024px):
//    If the screen is too narrow for all links to fit horizontally without crowding,
//    it cleanly shifts to the hamburger navigation drawer.
//
// 4. ACCESSIBLE MOBILE DRAWER:
//    The drawer includes all navigation links, the language switcher, and the primary CTA.
// =============================================================================

import { useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useScrolled } from '../hooks/useScrolled.js';
import { useMediaQuery, BREAKPOINT_LG } from '../hooks/useMediaQuery.js';
import { CloseIcon } from './icons.js';
import './Navbar.css';

export interface NavLink {
  /** Display text (should be translated via i18n) */
  label: string;
  /** URL path (e.g., "#menu", "#about", "#contact") */
  href: string;
  /** Unique key for React rendering */
  id: string;
}

export interface NavbarProps {
  /** The restaurant's display name or logo */
  siteName: string;
  /** Optional logo image URL */
  logo?: string;
  /** Optional brand icon element (e.g. musical acoustic guitar emblem) */
  brandIcon?: React.ReactNode;
  /** Navigation links */
  links: NavLink[];
  /** Primary CTA button text (e.g., "Reserve a Table", "Order Online") */
  ctaText?: string;
  /** Primary CTA button URL */
  ctaHref?: string;
  /** Language switcher React element */
  languageSwitcher?: React.ReactNode;
}

export function Navbar({
  siteName,
  logo,
  brandIcon,
  links,
  ctaText,
  ctaHref,
  languageSwitcher,
}: NavbarProps) {
  const isScrolled = useScrolled(40);
  const isDesktop = useMediaQuery(BREAKPOINT_LG); // ≥ 1024px
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Close mobile menu when resizing to desktop
  useEffect(() => {
    if (isDesktop) {
      setIsMenuOpen(false);
    }
  }, [isDesktop]);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isMenuOpen) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isMenuOpen]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  const toggleMenu = useCallback(() => {
    setIsMenuOpen((prev) => !prev);
  }, []);

  const closeMenu = useCallback(() => {
    setIsMenuOpen(false);
  }, []);

  return (
    <header
      className={`navbar ${isScrolled ? 'is-scrolled' : 'is-top'} ${isMenuOpen ? 'menu-open' : ''}`}
      role="banner"
    >
      <nav className="navbar-inner container" aria-label="Main navigation">
        {/* --- Brand / Logo --- */}
        <a href="/" className="navbar-brand" aria-label={`${siteName} - Home`}>
          {brandIcon && <span className="navbar-brand-icon">{brandIcon}</span>}
          {logo ? (
            <img
              src={logo}
              alt={`${siteName} logo`}
              className="navbar-logo"
              width={140}
              height={40}
            />
          ) : (
            <span className="navbar-name navbar-strum-text" aria-label={siteName}>
              {siteName.split('').map((char, index) => (
                <span
                  key={index}
                  className={`navbar-strum-char ${char === ' ' ? 'navbar-strum-space' : ''}`}
                  style={{ '--char-index': index } as React.CSSProperties}
                  aria-hidden="true"
                >
                  {char === ' ' ? '\u00A0' : char}
                </span>
              ))}
            </span>
          )}
        </a>

        {/* --- Desktop Nav Links (Visible only on ≥ 1024px) --- */}
        <ul className="navbar-links" role="list">
          {links.map((link) => (
            <li key={link.id}>
              <a
                href={link.href}
                className="navbar-link navbar-strum-text"
                aria-label={link.label}
              >
                {link.label.split('').map((char, index) => (
                  <span
                    key={index}
                    className={`navbar-strum-char ${char === ' ' ? 'navbar-strum-space' : ''}`}
                    style={{ '--char-index': index } as React.CSSProperties}
                    aria-hidden="true"
                  >
                    {char === ' ' ? '\u00A0' : char}
                  </span>
                ))}
              </a>
            </li>
          ))}
        </ul>

        {/* --- Right Actions (Language Switcher, CTA, Hamburger) --- */}
        <div className="navbar-actions">
          {/* Language Switcher */}
          {languageSwitcher && <div className="navbar-lang-slot">{languageSwitcher}</div>}

          {/* Primary CTA (Visible on desktop & tablet) */}
          {ctaText && ctaHref && (
            <a href={ctaHref} className="btn btn-primary navbar-cta">
              {ctaText}
            </a>
          )}

          {/* Hamburger Toggle with 3 dephased guitar strings (< 1024px) */}
          <button
            type="button"
            className={`navbar-hamburger ${isMenuOpen ? 'open' : ''}`}
            onClick={toggleMenu}
            aria-expanded={isMenuOpen}
            aria-controls="mobile-navigation-drawer"
            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
          >
            <svg
              className={`hamburger-strings-icon ${isMenuOpen ? 'is-open' : ''}`}
              viewBox="0 0 28 20"
              width="28"
              height="20"
              aria-hidden="true"
            >
              {/* Guitar String 1 (Top wave) */}
              <g className="hamburger-string-wrap string-1">
                <path
                  className="hamburger-string"
                  d="M-16,4 Q-12,2 -8,4 T0,4 T8,4 T16,4 T24,4 T32,4 T40,4 T48,4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                />
              </g>
              {/* Guitar String 2 (Middle wave - slight phase shift) */}
              <g className="hamburger-string-wrap string-2">
                <path
                  className="hamburger-string"
                  d="M-16,10 Q-12,8 -8,10 T0,10 T8,10 T16,10 T24,10 T32,10 T40,10 T48,10"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                />
              </g>
              {/* Guitar String 3 (Bottom wave - slight phase shift) */}
              <g className="hamburger-string-wrap string-3">
                <path
                  className="hamburger-string"
                  d="M-16,16 Q-12,14 -8,16 T0,16 T8,16 T16,16 T24,16 T32,16 T40,16 T48,16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                />
              </g>
            </svg>
          </button>
        </div>
      </nav>

      {/* Organic sinusoidal wave divider at the bottom of the navbar */}
      <div className="navbar-wave-divider" aria-hidden="true">
        <svg
          className="navbar-wave-svg"
          viewBox="0 0 1200 24"
          preserveAspectRatio="none"
        >
          <path
            className="navbar-wave-path"
            d="M0,0 L1200,0 L1200,6 Q1050,22 900,6 T600,12 T300,6 T0,14 Z"
          />
        </svg>
      </div>

      {/* --- Fullscreen Mobile Navigation Drawer (mounted via Portal) --- */}
      {isMenuOpen &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            className="navbar-mobile-drawer"
            id="mobile-navigation-drawer"
            role="dialog"
            aria-modal="true"
            aria-label="Navigation menu"
          >
            <div className="mobile-drawer-header">
              <span className="mobile-drawer-brand">{siteName}</span>
              <button
                type="button"
                className="mobile-drawer-close"
                onClick={closeMenu}
                aria-label="Close navigation"
              >
                <CloseIcon width={24} height={24} />
              </button>
            </div>

            <div className="mobile-drawer-content">
              <ul className="mobile-drawer-links" role="list">
                {links.map((link) => (
                  <li key={link.id}>
                    <a
                      href={link.href}
                      className="mobile-drawer-link navbar-strum-text"
                      onClick={closeMenu}
                      aria-label={link.label}
                    >
                      {link.label.split('').map((char, index) => (
                        <span
                          key={index}
                          className={`navbar-strum-char ${char === ' ' ? 'navbar-strum-space' : ''}`}
                          style={{ '--char-index': index } as React.CSSProperties}
                          aria-hidden="true"
                        >
                          {char === ' ' ? '\u00A0' : char}
                        </span>
                      ))}
                    </a>
                  </li>
                ))}
              </ul>

              <div className="mobile-drawer-footer">
                {ctaText && ctaHref && (
                  <a
                    href={ctaHref}
                    className="btn btn-primary btn-lg mobile-drawer-cta"
                    onClick={closeMenu}
                  >
                    {ctaText}
                  </a>
                )}

                {languageSwitcher && <div className="mobile-drawer-lang">{languageSwitcher}</div>}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </header>
  );
}
